import type {
    Message,
    MessageMetadata,
    WhatsAppErrorPayload,
} from "~/models/Chat";

export type MessageErrorType =
    | "recipient"
    | "account"
    | "template"
    | "transient"
    | "unknown";

export interface MessageError {
    code: string | null;
    type: MessageErrorType;
    summary: string | null;
    detail: string | null;
    referenceUrl: string | null;
    traceId: string | null;
    isTransient: boolean;
    source: "delivery" | "form_conversation";
}

const TYPE_BY_CODE: Record<string, MessageErrorType> = {
    "130403": "recipient",
    "130472": "recipient",
    "131021": "recipient",
    "131026": "recipient",
    "131030": "recipient",
    "131047": "recipient",
    "131049": "recipient",
    "131050": "recipient",
    "131056": "recipient",

    "0": "account",
    "3": "account",
    "10": "account",
    "33": "account",
    "190": "account",
    "200": "account",
    "368": "account",
    "130497": "account",
    "131005": "account",
    "131031": "account",
    "131037": "account",
    "131042": "account",
    "131045": "account",
    "131048": "account",
    "131063": "account",
    "131064": "account",
    "133010": "account",
    "134011": "account",

    "100": "template",
    "131008": "template",
    "131009": "template",
    "131051": "template",
    "131053": "template",
    "132000": "template",
    "132001": "template",
    "132005": "template",
    "132007": "template",
    "132012": "template",
    "132015": "template",
    "132016": "template",
    "132018": "template",
    "132068": "template",
    "135000": "template",

    "1": "transient",
    "2": "transient",
    "4": "transient",
    "80007": "transient",
    "130429": "transient",
    "131000": "transient",
    "131016": "transient",
    "131052": "transient",
    "131057": "transient",
    "132069": "transient",
    "133004": "transient",
    "2494055": "transient",
};

const CODES_WITH_SPECIFIC_DETAIL = new Set([
    "100",
    "131008",
    "131009",
    "132000",
    "132001",
    "132005",
    "132007",
    "132012",
    "132015",
    "132018",
    "135000",
]);

const WHATSAPP_CODE_PREFIX = /^\(#\d+\)\s*/;
const WHATSAPP_DOCS_HOST = "developers.facebook.com";

const REFERENCE_LABELS: { match: (url: URL) => boolean; key: string }[] = [
    {
        match: (url) => url.pathname.startsWith("/billing_hub"),
        key: "whatsapp_errors.references.billing",
    },
    {
        match: (url) => url.hostname.endsWith("business.facebook.com"),
        key: "whatsapp_errors.references.business_manager",
    },
];

export function findReferenceTranslationKey(
    referenceUrl?: string | null,
): string {
    if (!referenceUrl) {
        return "whatsapp_errors.references.generic";
    }

    try {
        const url = new URL(referenceUrl);

        return (
            REFERENCE_LABELS.find((label) => label.match(url))?.key ??
            "whatsapp_errors.references.generic"
        );
    } catch {
        return "whatsapp_errors.references.generic";
    }
}

function collapseWhitespace(value: unknown): string | null {
    if (typeof value !== "string") {
        return null;
    }

    const collapsed = value.replace(/\s+/g, " ").trim();

    return collapsed.length ? collapsed : null;
}

function toComparableText(value: string): string {
    return value.toLowerCase().replace(/[.\s]+$/, "");
}

function parseActionableUrl(value: unknown): string | null {
    const candidate = collapseWhitespace(value);

    if (!candidate || candidate === "null") {
        return null;
    }

    try {
        const url = new URL(candidate);

        if (!["http:", "https:"].includes(url.protocol)) {
            return null;
        }

        return url.hostname.endsWith(WHATSAPP_DOCS_HOST) ? null : url.href;
    } catch {
        return null;
    }
}

function readErrorPayload(
    metadata?: MessageMetadata | null,
    preferDelivery = false,
): { payload: WhatsAppErrorPayload; source: MessageError["source"] } | null {
    const data = metadata?.data;

    if (!data || Array.isArray(data) || !Object.keys(data).length) {
        return null;
    }

    const formConversationError = data.message_error;
    const hasDeliveryError = ["code", "message", "title", "error_data"].some(
        (key) => key in data,
    );

    if (
        (!preferDelivery || !hasDeliveryError) &&
        formConversationError &&
        typeof formConversationError === "object" &&
        !Array.isArray(formConversationError)
    ) {
        return {
            payload: formConversationError as WhatsAppErrorPayload,
            source: "form_conversation",
        };
    }

    return {
        payload: data as WhatsAppErrorPayload,
        source: "delivery",
    };
}

export function parseMessageError(
    metadata?: MessageMetadata | null,
    preferDelivery = false,
): MessageError {
    const resolvedPayload = readErrorPayload(metadata, preferDelivery);
    const payload = resolvedPayload?.payload;

    const code =
        payload?.code === null || payload?.code === undefined
            ? null
            : String(payload.code).trim() || null;

    const summary =
        collapseWhitespace(payload?.message)?.replace(
            WHATSAPP_CODE_PREFIX,
            "",
        ) ?? collapseWhitespace(payload?.title);

    const referenceUrl = parseActionableUrl(payload?.href);
    const isRecognisedCode = code !== null && code in TYPE_BY_CODE;

    let detail =
        !isRecognisedCode || CODES_WITH_SPECIFIC_DETAIL.has(code ?? "")
            ? collapseWhitespace(payload?.error_data?.details)
            : null;

    if (detail && referenceUrl && detail.includes(referenceUrl)) {
        const beforeUrl = detail.slice(0, detail.indexOf(referenceUrl));
        const sentenceEnd = beforeUrl.lastIndexOf(". ");

        detail = collapseWhitespace(
            sentenceEnd === -1
                ? beforeUrl
                : beforeUrl.slice(0, sentenceEnd + 1),
        );
    }

    const detailRepeatsSummary = [
        summary,
        collapseWhitespace(payload?.title),
    ].some(
        (other) =>
            other &&
            detail &&
            toComparableText(other) === toComparableText(detail),
    );

    return {
        code,
        type: (code && TYPE_BY_CODE[code]) || "unknown",
        summary: isRecognisedCode ? null : summary,
        detail: detailRepeatsSummary ? null : detail,
        referenceUrl,
        traceId: collapseWhitespace(payload?.fbtrace_id),
        isTransient: payload?.is_transient === true,
        source: resolvedPayload?.source ?? "delivery",
    };
}

export const MESSAGE_ERROR_TYPES: MessageErrorType[] = [
    "recipient",
    "account",
    "template",
    "transient",
    "unknown",
];

export function findMessageErrorType(
    code?: string | number | null,
): MessageErrorType {
    if (code === null || code === undefined || code === "") {
        return "unknown";
    }

    return TYPE_BY_CODE[String(code).trim()] ?? "unknown";
}

export function isFailedMessage(
    message?: Pick<Message, "status"> | null,
): boolean {
    return ["error", "failed"].includes(message?.status?.value ?? "");
}

export function hasMessageError(message?: Message | null): boolean {
    return (
        isFailedMessage(message) ||
        readErrorPayload(message?.metadata)?.source === "form_conversation"
    );
}

export function findMessageErrorTranslationKey(
    code?: string | null,
): string | null {
    if (!code) {
        return null;
    }

    const { te, defaultLocale } = useI18n();
    const key = `whatsapp_errors.codes.${code}`;

    return te(`${key}.label`) || te(`${key}.label`, defaultLocale) ? key : null;
}

export function translateMessageError(
    error: { code?: string | null },
    translate: (key: string) => string,
): { label: string; description: string; isKnown: boolean } {
    const key = findMessageErrorTranslationKey(error.code);

    return {
        label: key
            ? translate(`${key}.label`)
            : translate("whatsapp_errors.unknown.label"),
        description: key
            ? translate(`${key}.description`)
            : translate("whatsapp_errors.unknown.description"),
        isKnown: !!key,
    };
}
